import React, { useState, useEffect, useRef } from "react";
import "./Checkout.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import BASE_URL from "../../components/link/link";
import useStudentId from "../../components/link/authutils";

const Checkout = () => {
  const navigate = useNavigate();
  const { studentId, isLoading } = useStudentId();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPaying, setIsPaying] = useState(false);

  // Use ref to always have latest values inside async callbacks
  const itemsRef = useRef([]);
  const studentIdRef = useRef(null);
  const amountRef = useRef(0);
  const addressRef = useRef({});
  const serverAmountRef = useRef(0); // authoritative amount (paise) returned by /payment/order

  const [address, setAddress] = useState({
    name: "",
    phone: "",
    addressLine: "",
    city: "",
    state: "",
    postalCode: "",
  });

  // Keep refs in sync with state
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    studentIdRef.current = studentId;
  }, [studentId]);

  useEffect(() => {
    addressRef.current = address;
  }, [address]);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const storedBuyNowItem = JSON.parse(localStorage.getItem("buyNowItem"));

        if (storedBuyNowItem) {
          setItems([storedBuyNowItem]);
        } else if (studentId) {
          const response = await fetch(`${BASE_URL}/cart/getCart/${studentId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          });

          if (!response.ok) {
            throw new Error("Failed to fetch cart items");
          }

          const data = await response.json();
          setItems(data.cart.items || []);
        }
      } catch (err) {
        console.error("Error fetching items:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (!isLoading) {
      fetchItems();
    }
  }, [studentId, isLoading]);

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    if (name === "name" && (!/^[A-Za-z\s]*$/.test(value) || value.length > 50))
      return;
    if (name === "phone" && (!/^\d*$/.test(value) || value.length > 10)) return;

    setAddress((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const amount = items.reduce(
    (total, item) => total + item.price * (item.quantity || 1),
    0
  );

  // Keep amountRef in sync
  useEffect(() => {
    amountRef.current = amount;
  }, [amount]);

  const handleCancel = async () => {
    try {
      localStorage.removeItem("buyNowItem");
      toast.info("Order cancelled", {
        position: "top-center",
        autoClose: 1500,
      });
      setTimeout(() => navigate("/materials"), 1500);
    } catch (err) {
      toast.error("Error cancelling order. Please try again.");
    }
  };

  // ✅ Retry helper — retries a function up to `maxAttempts` times
  const withRetry = async (fn, maxAttempts = 3, delayMs = 1000) => {
    let lastError;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await fn();
      } catch (err) {
        lastError = err;
        console.warn(`Attempt ${attempt}/${maxAttempts} failed:`, err.message);
        if (attempt < maxAttempts) {
          await new Promise((r) => setTimeout(r, delayMs * attempt));
        }
      }
    }
    throw lastError;
  };

  // ✅ saveOrder uses refs so it always has latest state inside Razorpay callback
  const saveOrder = async (paymentDetails) => {
    const currentStudentId = studentIdRef.current;
    const currentItems = itemsRef.current;
    const currentAmount = amountRef.current;
    const currentAddress = addressRef.current;

    // Validate critical fields before making any API call
    if (!currentStudentId) throw new Error("Student ID is missing. Please log in again.");
    if (!paymentDetails?.razorpay_payment_id) throw new Error("Payment ID is missing.");
    if (!paymentDetails?.razorpay_order_id) throw new Error("Order ID is missing.");
    if (!currentItems || currentItems.length === 0) throw new Error("No items found in order.");

    const orderData = {
      studentId: currentStudentId,
      Items: currentItems.map((item) => ({
        // Material id: cart items carry it as `productId` (their `_id` is the
        // cart line-item id), buy-now items are the material itself (`_id`).
        productId: item.productId || item._id,
        name: item.name,
        price: item.price,
        quantity: item.quantity || 1,
        imageUrl: item.banner || item.imageUrl,
        subtopic: item.subtopic,
      })),
      // Save what Razorpay actually charged (server-priced); fall back to the
      // displayed total only if the server amount is somehow unavailable.
      totalAmount: serverAmountRef.current ? serverAmountRef.current / 100 : currentAmount,
      Address: {
        name: currentAddress.name,
        phone: currentAddress.phone,
        addressLine: currentAddress.addressLine,
        city: currentAddress.city,
        state: currentAddress.state,
        postalCode: currentAddress.postalCode,
      },
      paymentId: paymentDetails.razorpay_payment_id,
      orderId: paymentDetails.razorpay_order_id,
    };

    // ✅ Retry saveOrder up to 3 times with increasing delay
    await withRetry(async () => {
      const response = await fetch(`${BASE_URL}/order/saveOrder`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Failed to save order: ${errorData}`);
      }

      return await response.json();
    }, 3, 1000);

    // ✅ Clear cart only after order is confirmed saved
    const isBuyNowItem = localStorage.getItem("buyNowItem");
    if (!isBuyNowItem) {
      try {
        const clearCartResponse = await fetch(
          `${BASE_URL}/cart/${currentStudentId}/clear`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }
        );
        if (!clearCartResponse.ok) {
          // Non-critical: log but don't throw — order is already saved
          console.warn("Cart clear failed, but order was saved successfully.");
        }
      } catch (cartErr) {
        // Non-critical error — order is saved, just log it
        console.warn("Cart clear error (non-critical):", cartErr.message);
      }
    }

    localStorage.removeItem("buyNowItem");
  };

  const handlePayVerify = (data) => {
    return new Promise((resolve, reject) => {
      const options = {
        key: "rzp_live_sFc64UI72d3NUw",
        amount: data.amount,
        currency: data.currency,
        name: "Shyam Institute",
        description: "Purchase",
        order_id: data.id,

        handler: async (response) => {
          try {
            // Step 1: Verify payment signature with backend
            const res = await fetch(`${BASE_URL}/payment/verify`, {
              method: "POST",
              headers: { "content-type": "application/json" },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });

            if (!res.ok) {
              throw new Error("Payment verification request failed");
            }

            const verifyData = await res.json();

            if (!verifyData.message) {
              throw new Error("Payment verification failed: invalid response");
            }

            // Step 2: Save order FIRST before showing any success message
            await saveOrder(response);

            // Step 3: Only now show success — order is confirmed saved
            toast.success("Payment successful! Order placed ✅", {
              position: "top-center",
              autoClose: 2500,
            });

            resolve();
            setTimeout(() => navigate("/"), 2500);

          } catch (err) {
            console.error("Payment handler error:", err);

            // Show error with payment ID so user/support can recover
            toast.error(
              `Payment was received but order save failed. Please contact support with Payment ID: ${response?.razorpay_payment_id || "N/A"}`,
              { position: "top-center", autoClose: 8000 }
            );

            // Log full details for debugging
            console.error("CRITICAL ORDER SAVE FAILURE:", {
              paymentId: response?.razorpay_payment_id,
              orderId: response?.razorpay_order_id,
              studentId: studentIdRef.current,
              error: err.message,
            });

            reject(err);
          }
        },

        modal: {
          // ✅ Handle user dismissing the modal — no crash, clean state
          ondismiss: () => {
            setIsPaying(false);
            toast.info("Payment cancelled. You can try again.", {
              position: "top-center",
              autoClose: 2000,
            });
            reject(new Error("Payment modal dismissed by user"));
          },
          escape: true,
          animation: true,
        },

        theme: { color: "#5f63b8" },
      };

      const rzp1 = new window.Razorpay(options);

      // ✅ Handle payment failures (card declined, bank error, etc.)
      rzp1.on("payment.failed", (response) => {
        console.error("Razorpay payment.failed event:", response.error);
        setIsPaying(false);
        toast.error(
          `Payment failed: ${response.error?.description || "Unknown error"}. Please try again.`,
          { position: "top-center", autoClose: 4000 }
        );
        reject(new Error(response.error?.description || "Payment failed"));
      });

      rzp1.open();
    });
  };

  const handlePay = async (amount) => {
    // Validate address fields
    const missingFields = Object.entries(address)
      .filter(([_, value]) => !value || value.trim() === "")
      .map(([key]) => {
        const labels = {
          name: "Name",
          phone: "Phone Number",
          addressLine: "Address Line",
          city: "City",
          state: "State",
          postalCode: "Postal Code",
        };
        return labels[key] || key;
      });

    if (missingFields.length > 0) {
      toast.error(`Please fill out: ${missingFields.join(", ")}`, {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    // Validate phone number length
    if (address.phone.length !== 10) {
      toast.error("Phone number must be exactly 10 digits.", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    if (typeof amount !== "number" || amount <= 0) {
      toast.error("Invalid payment amount.");
      return;
    }

    if (!studentId) {
      toast.error("Session expired. Please log in again.");
      return;
    }

    // ✅ Prevent double-click / double payment
    if (isPaying) return;
    setIsPaying(true);

    try {
      // Step 1: Create Razorpay order on backend.
      // Send the items so the server computes the authoritative amount from DB
      // prices (the client amount is only a fallback / display value).
      // Use the material id: cart items carry it as `productId` (their `_id` is
      // the cart line-item id), buy-now items are the material itself (`_id`).
      const orderItems = items.map((it) => ({
        productId: it.productId || it._id,
        quantity: it.quantity || 1,
      }));
      const res = await fetch(`${BASE_URL}/payment/order`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ items: orderItems, amount: Number(amount) * 100 }),
      });

      if (!res.ok) {
        throw new Error("Failed to create payment order. Please try again.");
      }

      const data = await res.json();

      if (!data || !data.data) {
        throw new Error("Invalid response from payment server.");
      }

      // Remember the server-priced amount so the saved order total matches the charge.
      serverAmountRef.current = data.data.amount;

      // Step 2: Open Razorpay and verify
      await handlePayVerify(data.data);

    } catch (error) {
      console.error("handlePay error:", error.message);

      // ✅ Do NOT reload — just show error so user can retry
      if (error.message !== "Payment modal dismissed by user") {
        toast.error(
          error.message || "Something went wrong. Please try again.",
          { position: "top-center", autoClose: 4000 }
        );
      }
    } finally {
      // ✅ Always re-enable the Pay button (unless navigating away)
      setIsPaying(false);
    }
  };

  if (isLoading || loading)
    return <div className="address-container">Loading...</div>;
  if (error)
    return <div className="address-container">Error: {error}</div>;
  if (items.length === 0)
    return <div className="address-container">No items to checkout</div>;

  return (
    <div className="address-container">
      <h1>Shipping Address</h1>
      <div className="address-form">
        <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
          <div className="form-group">
            <label>
              Name<span className="required">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={address.name}
              onChange={handleAddressChange}
              required
              autoComplete="off"
              placeholder="Full Name"
            />
          </div>
          <div className="form-group">
            <label>
              Phone Number<span className="required">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={address.phone}
              onChange={handleAddressChange}
              maxLength={10}
              required
              autoComplete="off"
              placeholder="10-digit mobile number"
            />
          </div>
          <div className="form-group">
            <label>
              Address Line<span className="required">*</span>
            </label>
            <input
              type="text"
              name="addressLine"
              value={address.addressLine}
              onChange={handleAddressChange}
              maxLength={150}
              required
              autoComplete="off"
              placeholder="House/Flat No, Street, Area"
            />
          </div>
          <div className="form-group">
            <label>
              City<span className="required">*</span>
            </label>
            <input
              type="text"
              name="city"
              value={address.city}
              onChange={handleAddressChange}
              required
              autoComplete="off"
              placeholder="City"
            />
          </div>
          <div className="form-group">
            <label>
              State<span className="required">*</span>
            </label>
            <input
              type="text"
              name="state"
              value={address.state}
              onChange={handleAddressChange}
              required
              autoComplete="off"
              placeholder="State"
            />
          </div>
          <div className="form-group">
            <label>
              Postal Code<span className="required">*</span>
            </label>
            <input
              type="text"
              name="postalCode"
              value={address.postalCode}
              onChange={handleAddressChange}
              required
              autoComplete="off"
              placeholder="6-digit PIN code"
              maxLength={6}
            />
          </div>
        </form>
      </div>

      {/* Order Summary */}
      <div className="order-summary">
        <h2>Order Summary</h2>
        {items.map((item) => (
          <div key={item._id || item.productId} className="order-item">
            <img
              src={item.banner || item.imageUrl}
              alt={item.name}
              className="order-item-image"
            />
            <div>
              <h3>{item.name}</h3>
              <div>
                <p>Quantity: {item.quantity || 1}</p>
                <p>Price: ₹{item.price}</p>
              </div>
            </div>
          </div>
        ))}
        <div className="total-amount-check">
          <h3>Total Amount: ₹{amount}</h3>
          <h4>(Included Courier Charges at Price Tag)</h4>
        </div>
      </div>

      <div className="checkout-buttons">
        <button
          className="cancel-button"
          onClick={handleCancel}
          disabled={isPaying}
        >
          Cancel Order
        </button>
        <button
          className="pay-button"
          onClick={() => handlePay(amount)}
          disabled={isPaying}
          style={{ opacity: isPaying ? 0.7 : 1, cursor: isPaying ? "not-allowed" : "pointer" }}
        >
          {isPaying ? "Processing..." : "Pay Now"}
        </button>
      </div>

      <ToastContainer />
    </div>
  );
};

export default Checkout;
